import { Loading } from "@lyfegen-platform/react-common";
import { Box, ButtonProps, Card, useTheme } from "@material-ui/core";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";
import { IDrawerData } from "../../store/lyfe-data-table";
import { FlexFormStatus } from "../../types/graphql-global-types";
import { isEmptyValue } from "../../utils/is-empty-value";
import { sumBy } from "../../utils/sum-by";
import LyfeDataRow from "./LyfeDataRow";
import LyfeTableHeader, { ActionOptionsProps } from "./LyfeTableHeader";
import { LyfeViewMore } from "./LyfeViewMore";
import useStyles from "./use-styles";
import getActiveDrawerState from "./utils/getActiveDrawerState";
import getActivePageByState from "./utils/getActivePageByState";
import getRowsPerPageStage from "./utils/getRowsPerPageState";

export type TableTypes =
	| "forms"
	| "records"
	| "cases"
	| "agreements"
	| "refunds"
	| "transactions"
	| "documents"
	| "publicAgreements"
	| "personal"
	| "my-roles"
	| "notifications"
	| "subscriptions"
	| "quests"
	| "simulations"
	| "models"
	| "comparison"
	| "users"
	| "domains";

const defaultHeaderActionsOptions: Array<ActionOptionsProps> = [{ label: "sortAsc" }, { label: "sortDesc" }];

export const checkBoxWidthPx = 70;
export const rowExpanderWidthPx = 30;
export const rowAddonWidthPx = 50;
export const actionButtonWidthPx = 50;

export interface ITableProps {
	real?: boolean;
	renderSubRow?: (data: any) => JSX.Element;
	renderRowAddon?: (data: any) => JSX.Element;
	hideRowHighlight?: boolean;
	toolbarProps?: {
		onFilterClick?: () => any;
		onUploadButtonClick?: () => any;
		onCreateButtonClick?: () => any;
		onActionsClick?: () => any;
		uploadButtonProps?: ButtonProps;
		createButtonProps?: ButtonProps;
		actionItems?: Array<{
			label: string;
			onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>, data: any) => void;
		}>;
	};
	headerActionsOptions?: Array<ActionOptionsProps>;
	page: number;
	rowsPerPage: number;
	pageSizeOptions?: Array<number>;
	totalRecords: number;
	filteredRecords: number | null | undefined;
	title: TableTypes;
	//TODO: make not Case-specific
	onRowClickAction?: (id: string | number | undefined, formType: string, status?: FlexFormStatus, lastVer?: any) => void;
	onCellClickAction?: (event: React.MouseEvent<Element, MouseEvent>, value: string | undefined) => void;
	onRowClick?: (rowData: any, event: React.MouseEvent<Element, MouseEvent> | undefined) => void;
	defaultSort?: {
		column: string;
		action: ActionOptionsProps["label"];
	};
	isHeaderCheckBoxChecked?: (checked: boolean) => void;
	onRowCheckBoxClick?: (data: any, index: number, checked: boolean) => void;
	checkBox?: boolean;
	showActionColumn?: boolean;
	actionIcon?: React.ReactElement;
	actionTooltip?: string;
	isActionEnabledByRow?: (data: unknown) => boolean;
	actionButtonHeader?: any;
	actionButtonCallback?: any;
	grayedOutActionRowIds?: Set<string>;
	isLoading: boolean;
	columns: Array<any>;
	data: Array<any>;
	allChecked?: boolean;
	checkedIds?: Array<string>;
	publicDrawerId?: string;
	rowTooltipTitle?: (data: any) => string;
	isRowDisabled?: (data: any) => boolean;
	isRowGrayedOut?: (data: any) => boolean;
	tableStyles?: CSSProperties;
	preHeader?: JSX.Element;
	enableRowHovering?: boolean;
	viewMoreState?: boolean;
	viewMoreLimit?: number;
	handleViewMoreClick?: (expanded: boolean) => void;
}

const LoadingRow = (props: { numOfColumns: number }) => (
	<tr>
		<td style={{ textAlign: "center" }} rowSpan={50} colSpan={props.numOfColumns}>
			<Box display="flex" justifyContent="center" height="500px" alignItems="center">
				<Loading />
			</Box>
		</td>
	</tr>
);

const NoDataRow = (props: { title: string; numOfColumns: number }) => {
	const { t } = useTranslation();

	return (
		<tr>
			<td style={{ textAlign: "center", height: "500px" }} rowSpan={50} colSpan={props.numOfColumns}>
				{t("no")} {t(props.title)} {t("to-display")}
			</td>
		</tr>
	);
};

function ActivePageUpdateEffect(props: { title: TableTypes; filteredRecords: number | null | undefined }) {
	const [activePage, setActivePage] = useRecoilState(getActivePageByState(props.title));
	const rowsPerPage = useRecoilValue(getRowsPerPageStage(props.title));

	useEffect(() => {
		if (rowsPerPage && !isEmptyValue(props.filteredRecords) && Math.ceil(props.filteredRecords! / rowsPerPage) - 1 < activePage) {
			setActivePage(0);
		}
	}, [props.title, props.filteredRecords]);

	return null;
}

const ROW_HEIGHT = 50;
const OVERSCAN = 5;

export default function LyfeDataTable(props: ITableProps) {
	const prevLen = useRef<number | undefined>();
	const { isLoading, renderSubRow, rowTooltipTitle, ...other } = props;
	const [checked, setChecked] = useState<Array<number | null>>([]);
	const [activeDrawer, setActiveDrawer] = useRecoilState<(IDrawerData & object) | false>(getActiveDrawerState(other.title));
	const { table } = useStyles({});
	const [checkAll, setCheckAll] = useState<boolean>(false);
	const hasCollapsibleRows = !!renderSubRow;
	const hasRowAddon = !!props.renderRowAddon;
	const numOfColumns = other.columns.length + (hasRowAddon ? 1 : 0) + (hasCollapsibleRows ? 1 : 0);
	const { shadows } = useTheme();

	const containerRef = useRef<HTMLTableSectionElement | null>(null);

	const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });

	useEffect(() => {
		const handleScroll = () => {
			if (!containerRef.current) return;

			const { top } = containerRef.current.getBoundingClientRect();
			const viewportHeight = window.innerHeight;

			const scrollTop = top < 0 ? Math.abs(top) : 0;

			const start = Math.floor(scrollTop / ROW_HEIGHT);
			const end = Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT);

			setVisibleRange({
				start: Math.max(0, start - OVERSCAN),
				end: Math.min(props.data.length, end + OVERSCAN),
			});
		};

		window.addEventListener("scroll", handleScroll);
		handleScroll();

		return () => window.removeEventListener("scroll", handleScroll);
	}, [props.data.length]);

	const { start, end } = visibleRange;

	const handleRowClick = useCallback(
		(event?: React.MouseEvent<Element, MouseEvent> | undefined, rowData?: IDrawerData & object) => {
			if (props.onRowClickAction) {
				return props.onRowClickAction(rowData?.id, (rowData as any).type, (rowData as any).status, (rowData as any).id);
			}

			props.onRowClick && props.onRowClick(rowData, event);

			rowData && !props.hideRowHighlight && setActiveDrawer(rowData);
		},
		[props.onRowClickAction, props.onRowClick, props.hideRowHighlight, setActiveDrawer]
	);

	const handleHeaderCheckBoxChange = (checked: boolean) => {
		setCheckAll(checked);
		props.isHeaderCheckBoxChecked && props.isHeaderCheckBoxChecked(checked);
		checked ? setChecked(other.data.map((r) => r.id)) : setChecked([]);
	};

	const handleRowCheckBoxChange = useCallback(
		(record: any, index: number, checked: boolean) => {
			props.onRowCheckBoxClick && props.onRowCheckBoxClick(record, index, checked);
			if (checked) {
				setChecked((e) => [...e, record.id]);
			} else {
				setCheckAll(false);
				setChecked((e) => e.filter((v) => record.id !== v));
			}
		},
		[props.onRowCheckBoxClick, setChecked, setCheckAll]
	);

	useEffect(() => {
		if (checkAll && other.data && other.data.length) {
			setChecked(other.data.map((r) => r.id));
		}
	}, [other.data]);

	useEffect(() => {
		prevLen.current = other.data.length;
	});

	const controlCellsWidth =
		(props.checkBox ? checkBoxWidthPx : 0) +
		(props.renderSubRow ? rowExpanderWidthPx : 0) +
		(props.renderRowAddon ? rowAddonWidthPx : 0) +
		(props.showActionColumn ? actionButtonWidthPx : 0);
	const columnActualWidths = useMemo(() => {
		const requestedWidths = props.columns.map((column) => (100 / props.columns.length) * (column.widthAdjustment || 1));
		const overallWidth = sumBy(requestedWidths, (width) => width);
		const normalizationQuotient = 100 / overallWidth;
		return requestedWidths.map((width) => width * normalizationQuotient);
	}, [props.columns]);

	const canBeExpanded = !!props.viewMoreLimit && props.data.length > props.viewMoreLimit;
	const shownData = canBeExpanded && !props.viewMoreState ? props.data.slice(0, props.viewMoreLimit) : props.data;

	return (
		<Card className={table} style={{ overflow: "initial", boxShadow: shadows[3], width: "100%" }}>
			<ActivePageUpdateEffect title={props.title} filteredRecords={props.filteredRecords} />
			{props.preHeader && props.preHeader}
			<table
				style={
					{
						minWidth: "800px",
						width: "100%",
						borderCollapse: "separate",
						tableLayout: "fixed",
						borderSpacing: "0px",
						...props.tableStyles,
					} as React.CSSProperties
				}
			>
				<colgroup>
					{props.checkBox && <col style={{ width: `${checkBoxWidthPx}px` }} />}
					{props.renderRowAddon && <col style={{ width: `${rowAddonWidthPx}px` }} />}
					{props.renderSubRow && <col style={{ width: `${rowExpanderWidthPx}px` }} />}
					{columnActualWidths.map((width, index) => (
						<col
							key={index}
							style={{
								width: props.columns[index].width
									? props.columns[index].width
									: `calc(${width}% - ${controlCellsWidth / props.columns.length}px)`,
							}}
						/>
					))}
					{props.showActionColumn && <col style={{ width: `${checkBoxWidthPx}px` }} />}
				</colgroup>
				<LyfeTableHeader
					hasCollapsibleRows={hasCollapsibleRows}
					hasRowAddon={hasRowAddon}
					defaultSort={props.defaultSort}
					headerActionsOptions={other.headerActionsOptions || defaultHeaderActionsOptions}
					title={other.title}
					checkBox={props.checkBox}
					isChecked={props.allChecked !== undefined ? props.allChecked : checkAll}
					handleHeaderCheckBoxChange={handleHeaderCheckBoxChange}
					columns={other.columns}
					showActionColumn={other.showActionColumn}
					actionButtonHeader={other.actionButtonHeader}
				/>
				<tbody ref={containerRef}>
					{isLoading && <LoadingRow numOfColumns={numOfColumns} />}
					{!isLoading && props.data.length === 0 && <NoDataRow numOfColumns={numOfColumns} title={props.title} />}
					{!isLoading &&
						props.data.length > 0 &&
						(shownData as Array<any>).map((r, i) => {
							return (
								<LyfeDataRow
									real={props.real}
									disabled={props.isRowDisabled && props.isRowDisabled(r)}
									grayedOut={props.isRowGrayedOut && props.isRowGrayedOut(r)}
									onClick={handleRowClick}
									onCellClickAction={other.onCellClickAction}
									renderSubRow={renderSubRow}
									renderAddon={props.renderRowAddon}
									rowTooltipTitle={rowTooltipTitle}
									columns={other.columns}
									data={r}
									isCheckBoxChecked={props.checkedIds ? props.checkedIds.includes(r.id) : checked.includes(r.id)}
									index={i}
									isActive={r.id === (activeDrawer as any)?.id || r.id === props.publicDrawerId}
									key={i}
									checkBox={props.checkBox}
									onRowCheckBoxClick={handleRowCheckBoxChange}
									enableHovering={props.enableRowHovering}
									showActionColumn={other.showActionColumn}
									actionButtonCallback={other.actionButtonCallback}
									isActionGrayedOut={props.grayedOutActionRowIds?.has(r.id)}
									actionIcon={props.actionIcon}
									actionTooltip={props.actionTooltip}
									isActionEnabledByRow={props.isActionEnabledByRow}
									isVisible={i >= start && i <= end}
								/>
							);
						})}
				</tbody>
			</table>
			{canBeExpanded && <LyfeViewMore expanded={!!props.viewMoreState} onClick={props.handleViewMoreClick} />}
		</Card>
	);
}
